import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Users, UserCheck, Ban, ShieldCheck } from "lucide-react";
import { getCustomers, getCustomer, patchCustomer, apiErr } from "../lib/api";
import { PageHead, Stat, Modal, Empty, Pill, BtnGhost } from "./_ui";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = (query) => { setLoading(true); getCustomers(query).then((d) => setRows(d.customers)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false)); };
  useEffect(() => { load(""); }, []);

  const onSearch = (e) => { e.preventDefault(); load(q.trim()); };

  const view = async (id) => { try { setDetail(await getCustomer(id)); } catch (e) { toast.error(apiErr(e)); } };

  const toggleStatus = async (c) => {
    const next = c.status === "suspended" ? "active" : "suspended";
    try {
      await patchCustomer(c.id, next);
      toast.success(next === "suspended" ? "Customer suspended" : "Customer reactivated");
      load(q.trim());
      if (detail?.customer?.id === c.id) setDetail((d) => ({ ...d, customer: { ...d.customer, status: next } }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const active = rows.filter((r) => r.status !== "suspended").length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-customers">
      <PageHead eyebrow="Demand side" title="Customers"
        desc="Everyone who signed up or requested a ceremony, with their activity and lifetime spend." />

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={Users} label="Total customers" value={rows.length} />
        <Stat icon={UserCheck} tint={{ bg: "#EEF2E6", fg: "#6D8B44" }} label="Active" value={active} />
        <Stat icon={ShieldCheck} tint={{ bg: "#E6F0EE", fg: "#2F7A6B" }} label="Total spend" value={inr(rows.reduce((s, r) => s + (r.spend || 0), 0))} />
      </div>

      <form onSubmit={onSearch} className="mt-6 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A79C8A]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search"
            placeholder="Search by name, email or phone" className="h-10 w-full rounded-lg border border-[#E5DFD3] pl-9 pr-3 font-body text-sm outline-none focus:border-[#E0A98F]" />
        </div>
        <button type="submit" className="rounded-lg bg-[#D84315] px-4 font-body text-sm font-semibold text-white hover:bg-[#BF360C]">Search</button>
      </form>

      <div className="mt-4 overflow-hidden rounded-2xl border border-[#E9E2D6] bg-white">
        <div className="hidden grid-cols-12 gap-3 border-b border-[#F0EBE1] bg-[#FAFAF5] px-4 py-3 font-body text-[11px] font-semibold uppercase tracking-wide text-[#A79C8A] md:grid">
          <span className="col-span-4">Customer</span><span className="col-span-3">Contact</span>
          <span className="col-span-1 text-center">Requests</span><span className="col-span-1 text-center">Bookings</span>
          <span className="col-span-2 text-right">Spend</span><span className="col-span-1 text-right">Status</span>
        </div>
        {rows.map((c) => (
          <button key={c.id} onClick={() => view(c.id)} data-testid={`customer-row-${c.id}`}
            className="grid w-full grid-cols-1 items-center gap-2 border-b border-[#F5F1E8] px-4 py-3 text-left last:border-0 hover:bg-[#FAF6EF] md:grid-cols-12 md:gap-3">
            <div className="md:col-span-4"><p className="font-body text-sm font-semibold text-[#2C241B]">{c.name || "—"}</p></div>
            <div className="font-body text-xs text-[#8A8072] md:col-span-3"><p>{c.email}</p>{c.phone && <p>{c.phone}</p>}</div>
            <div className="font-body text-sm text-[#5C5449] md:col-span-1 md:text-center">{c.requests_count}</div>
            <div className="font-body text-sm text-[#5C5449] md:col-span-1 md:text-center">{c.bookings_count}</div>
            <div className="font-body text-sm font-semibold text-[#2C241B] md:col-span-2 md:text-right">{inr(c.spend)}</div>
            <div className="md:col-span-1 md:text-right"><Pill status={c.status || "active"} /></div>
          </button>
        ))}
        {!loading && rows.length === 0 && <div className="px-4 py-16"><Empty>No customers found.</Empty></div>}
      </div>

      {detail && (
        <Modal title={detail.customer?.name || detail.customer?.email} onClose={() => setDetail(null)} testid="customer-modal"
          footer={<>
            <BtnGhost onClick={() => setDetail(null)}>Close</BtnGhost>
            <button onClick={() => toggleStatus(detail.customer)} data-testid="customer-toggle-status"
              className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 font-body text-sm font-semibold ${detail.customer.status === "suspended" ? "bg-[#E6F0EE] text-[#2F7A6B]" : "bg-[#FDECEA] text-[#C62828]"}`}>
              {detail.customer.status === "suspended" ? <><UserCheck size={15} /> Reactivate</> : <><Ban size={15} /> Suspend</>}
            </button>
          </>}>
          <div className="grid grid-cols-2 gap-3 font-body text-sm">
            <div><p className="text-xs text-[#A79C8A]">Email</p><p className="text-[#2C241B]">{detail.customer.email}</p></div>
            <div><p className="text-xs text-[#A79C8A]">Phone</p><p className="text-[#2C241B]">{detail.customer.phone || "—"}</p></div>
            <div><p className="text-xs text-[#A79C8A]">Status</p><Pill status={detail.customer.status || "active"} /></div>
            <div><p className="text-xs text-[#A79C8A]">Bookings</p><p className="text-[#2C241B]">{detail.bookings.length}</p></div>
          </div>
          <h4 className="mt-5 font-heading text-base font-semibold text-[#2C241B]">Requests ({detail.requests.length})</h4>
          <div className="mt-2 space-y-2">
            {detail.requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-[#F0EBE1] px-3 py-2 font-body text-sm">
                <span className="text-[#2C241B]">{r.pooja_name} · {r.date}</span><Pill status={r.status} />
              </div>
            ))}
            {detail.requests.length === 0 && <p className="font-body text-xs text-[#A79C8A]">No requests.</p>}
          </div>
          <h4 className="mt-5 font-heading text-base font-semibold text-[#2C241B]">Bookings ({detail.bookings.length})</h4>
          <div className="mt-2 space-y-2">
            {detail.bookings.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg border border-[#F0EBE1] px-3 py-2 font-body text-sm">
                <span className="text-[#2C241B]">{b.pooja_name || b.ceremony} · {inr(b.amount)}</span><Pill status={b.status} />
              </div>
            ))}
            {detail.bookings.length === 0 && <p className="font-body text-xs text-[#A79C8A]">No bookings.</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
